import { useLazyQuery } from "@apollo/client/react";
import { datadogRum } from "@datadog/browser-rum";
import mixpanel from "mixpanel-browser";
import { createContext, useContext, useEffect } from "react";

import { useAuth } from "./use-auth";
import { useWatchUserWsEvent } from "./use-watch-user-ws-event";
import { writeCachedConsentStatus } from "../components/consent/consentStatusCache";
import query from "../graphql/queries/CurrentUser";
import { GetCurrentUserQuery } from "../graphql/queries/CurrentUser.generated";

const CurrentUserContext = createContext<{
  data: GetCurrentUserQuery | null;
  user: GetCurrentUserQuery["user"] | null;
  loading: boolean;
  refetch: () => Promise<void>;
}>(null!);

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useCurrentUser = () => useContext(CurrentUserContext);

export function CurrentUserProvider(props: React.PropsWithChildren) {
  const [getUser, { data, refetch, loading, called }] = useLazyQuery(
    query,
    {
      fetchPolicy: "no-cache",
    },
  );
  const { userUuid } = useAuth();

  // Only call refetch if the lazy query has been executed at least once
  const safeRefetch = () => {
    if (called) {
      refetch();
    }
  };

  useWatchUserWsEvent({
    onMessage: safeRefetch,
    eventName: "user_update",
  });

  useEffect(() => {
    if (userUuid) {
      getUser();
    }
  }, [userUuid, getUser]);

  // mixpanel.identify()
  useEffect(() => {
    if (!userUuid) {
      if (mixpanel.get_distinct_id().startsWith("$device:")) return;

      // User is logged out, reset everything
      mixpanel.reset();
      mixpanel.register({
        "Web App Version": import.meta.env.VITE_APP_VERSION,
        platform: "web",
      });
    } else {
      if (mixpanel.get_distinct_id() === userUuid) return;

      // User is logged in, re-identify everything
      mixpanel.identify(userUuid);
    }
  }, [userUuid]);

  // Recording-consent gate fallback: cache the consent status carried on
  // every successful current-user resolution so useRecordingConsent can fall
  // back to the last-known server truth when this query is unavailable.
  useEffect(() => {
    writeCachedConsentStatus(userUuid, data?.user);
  }, [data, userUuid]);

  // mixpanel user details
  useEffect(() => {
    const user = data?.user;
    if (!user) return;
    mixpanel.people.set({
      $email: user.email,
      $first_name: user.firstName,
      $last_name: user.lastName,
    });
  }, [data]);

  // Google Analytics user_id
  useEffect(() => {
    window.gtag("set", { user_id: userUuid || null });
  }, [userUuid]);

  // Datadog RUM user identification
  useEffect(() => {
    if (!userUuid) {
      datadogRum.clearUser();
    } else {
      datadogRum.setUser({ id: userUuid });
    }
  }, [userUuid]);

  return (
    <CurrentUserContext.Provider
      value={{
        data: data || null,
        user: data?.user || null,
        loading,
        refetch: () => refetch().then(),
      }}
    >
      {props.children}
    </CurrentUserContext.Provider>
  );
}
